import React from "react";
import { Form } from "formik";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { FormLabel } from "../../../common/components/styled";
import FormError from "../../../common/components/FormControls/FormError";
import AlertMessage from "../../../common/components/AlertMessage";
import { routes } from "../../../routes";
import ButtonOutlined from "../../../common/components/Buttons/ButtonOutlined";
import {
  getParagraph14BoldTypography,
  getParagraph14RegularTypography,
} from "../../../common/Typography";
import StaticLink from "../../../common/components/StaticLink/StaticLink";
import {
  FormControl,
  FormHiddenInput,
  FormInput,
} from "../../../common/components/FormControls";

const Root = styled.div``;

const FieldsContainer = styled.div`
  display: grid;
  row-gap: 37px;
  padding-bottom: 41px;
`;

const FormRow = styled.div`
  width: 100%;
`;

const ForgotPasswordContainer = styled.div`
  display: flex;
  justify-content: space-between;
  padding-top: 36px;
`;

const NotMemberContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

const ForgotPasswordLink = styled(StaticLink)`
  ${getParagraph14RegularTypography};
  line-height: 18px;
  color: ${({ theme }) => theme.primaryColor};
  opacity: 0.7;
  text-decoration: none;

  &:hover {
    opacity: 1;
  }
`;

const NotMemberText = styled.p`
  ${getParagraph14RegularTypography};
  color: ${({ theme }) => theme.primaryColor};
  opacity: 0.7;
`;

const SignUpNowLink = styled(StaticLink)`
  ${getParagraph14BoldTypography};
  color: ${({ theme }) => theme.primaryColor};
  text-decoration: underline;
`;

const ErrorWrapper = styled.div`
  padding-bottom: 20px;
`;

interface ILoginForm {
  isSubmitting: boolean;
  errors: any;
  touched: any;
  error: any;
  onCloseError: () => void;
  onSubmit: (e: any) => void;
}

const LoginForm = ({
  isSubmitting,
  errors,
  touched,
  onCloseError,
  error,
}: ILoginForm) => {
  const { t } = useTranslation();

  return (
    <Root>
      {!!error && (
        <ErrorWrapper>
          <AlertMessage isOpen message={error} onClose={onCloseError} />
        </ErrorWrapper>
      )}

      <Form noValidate>
        <FieldsContainer>
          <FormRow>
            <FormControl>
              <FormLabel htmlFor="email">{t("email")}</FormLabel>
              <FormInput name="email" type="email" />
              <FormError error={t(errors.email!)} touched={touched.email} />
            </FormControl>
          </FormRow>

          <FormRow>
            <FormControl>
              <FormLabel htmlFor="password">{t("password")}</FormLabel>
              <FormHiddenInput name="password" />
              <FormError
                error={t(errors.password!)}
                touched={touched.password}
              />
            </FormControl>
          </FormRow>
        </FieldsContainer>
        <ButtonOutlined
          fullWidth
          type="submit"
          text={t("login-button-label")}
          height="48px"
          isLoading={isSubmitting}
        />
        <ForgotPasswordContainer>
          <ForgotPasswordLink href={routes.auth.forgotPassword}>
            {t("forgot-password")}
          </ForgotPasswordLink>
          <NotMemberContainer>
            <NotMemberText>{t("login-not-a-member")}</NotMemberText>
            <SignUpNowLink href={routes.auth.join.root}>
              {t("login-sign-up-now")}
            </SignUpNowLink>
          </NotMemberContainer>
        </ForgotPasswordContainer>
      </Form>
    </Root>
  );
};

export default LoginForm;
